import { Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import {
  DwMessageSeverity,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryParam,
  DwRoutingService,
  DwSecurityRegistrationRepositoryService,
  DwSecurityStateService,
  DwSecurityUserRegistration,
  DwStartRegistrationRequest,
  DwVerifyRegistrationEmailResult
} from '@devwareapps/devware-cap';
import { Observable, of, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  AcquisitionSourceAllItems,
  AppMetaDataLookups,
  JoinRequestTypeAllItems,
  PilotTypeAllItems
} from '../../../../meta-data/app-meta-data.service';
import { AviatorLoginPageComponent } from '../aviator-login-page/aviator-login-page.component';
import {
  FlightSchoolPositionLookupDisplayItem,
  LookupDisplayItem,
  PilotTypeLookupDisplayItem,
  RegisterUserAttemptResultLike,
  RegistrationAccountDraft,
  RegistrationComponentLike,
  RegistrationDraftState,
  RegistrationStep,
  RegistrationVerificationSession,
  SignupProfileDraft,
  SignupProfileFieldKey,
  SignupProfileRequiredField
} from '../../models/aviator-registration-page.model';
import { AviatorRegistrationDraftService } from '../../services/aviator-registration-draft.service';
import { IntegrationUtilService } from '../../../integration/services/integration-util.service';
import { StripeSessionDetailsResult } from '../../../payments/models/stripe-session-details-result.model';
import { PaymentsRepositoryService } from '../../../payments/services/payments-repository.service';
import { SchoolRepositoryService } from '../../../schools/services/school-repository.service';
import { PhoneNumberFormatService } from '../../../shared/services/phone-number-format.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-aviator-registration-page',
  templateUrl: './aviator-registration-page.component.html',
  styleUrls: ['./aviator-registration-page.component.scss']
})
export class AviatorRegistrationPageComponent extends AviatorLoginPageComponent {
  readonly allowRegistrationVerificationSkip = !!(environment as any).allowRegistrationVerificationSkip;
  readonly defaultCountryId = 4;
  readonly useStateLookup = true;
  readonly flightSchoolPilotTypeId = PilotTypeAllItems.FlightSchool;
  readonly countryLookupId = AppMetaDataLookups.CountryAllItems;
  readonly usStateLookupId = AppMetaDataLookups.StateProvideStateCodeByCountry;
  readonly internationalStateLookupId = AppMetaDataLookups.StateProvinceByCountry;
  readonly pilotTypeLookupId = AppMetaDataLookups.PilotTypeAllItems;
  readonly flightSchoolPositionLookupId = AppMetaDataLookups.FlightSchoolPositionAllItems;
  readonly acquisitionSourceLookupId = AppMetaDataLookups.AcquisitionSourceAllItems;
  readonly aviationGoalLookupId = AppMetaDataLookups.AviationGoalAllItems;
  readonly studentRegistrationTypeId = JoinRequestTypeAllItems.StudentRequest;
  readonly instructorRegistrationTypeId = JoinRequestTypeAllItems.FlightInstructor;

  private readonly registrationStepSequence: RegistrationStep[] = ['account', 'verification', 'profile'];
  private readonly resendCooldownDurationSeconds = 30;
  private readonly verificationSessionDurationMs = 10 * 60 * 1000;
  readonly verificationCodeSlots = [0, 1, 2, 3, 4, 5];

  registrationStep: RegistrationStep = 'account';
  signingIn = false;
  private signupSecuritySubscription: Subscription;
  verificationCode = '';
  verificationMessage = '';
  verificationEmail = '';
  verificationCodeInputFocused = false;
  registrationEmailDomainMessage = '';
  private restrictRegistrationEmailDomains = false;
  private allowedRegistrationEmailDomains: string[] = [];
  registrationHeading = 'Create your account';
  registrationSubheading = 'Start training with AviatorPro';
  showRegistrationRoleSelection = false;
  resendCooldownSeconds = 0;
  resendConfirmationVisible = false;
  private resendCooldownIntervalId: number | null = null;
  private verificationSession: RegistrationVerificationSession | null = null;

  showCountryLookup = false;
  showAcquisitionSourceOther = false;
  requirePilotCertificateNumber = false;
  requireInstructorCertificateNumber = false;
  profileValidationAttempted = false;
  profileFieldErrors: { [key: string]: string } = {};
  signupProfile: SignupProfileDraft = {
    birthDate: '',
    phoneNumber: '',
    city: '',
    state: '',
    stateId: null,
    zip: '',
    countryId: this.defaultCountryId,
    pilotTypeId: null,
    flightSchoolPositionId: null,
    acquisitionSourceId: null,
    acquisitionSourceOther: '',
    aviationGoalId: null,
    pilotCertificateNumber: '',
    instructorCertifcateNumber: '',
    requestedJoinRequestTypeId: null
  };
  private pilotTypeLookupItems: PilotTypeLookupDisplayItem[] = [];
  private flightSchoolPositionLookupItems: FlightSchoolPositionLookupDisplayItem[] = [];
  private registrationRef: RegistrationComponentLike | null = null;
  private persistedRegistrationDraft: RegistrationDraftState | null = null;
  private draftListenerCleanup: Array<() => void> = [];

  private profileRequiredFields: SignupProfileRequiredField[] = [
    { field: 'birthDate', label: 'Date of birth' },
    { field: 'phoneNumber', label: 'Phone number' },
    { field: 'city', label: 'City' },
    { field: 'zip', label: 'Zip' },
    { field: 'pilotTypeId', label: 'Pilot type' },
    { field: 'acquisitionSourceId', label: 'Attribution' },
    { field: 'aviationGoalId', label: 'Aviation goal' }
  ];

  constructor(
    integrationUtilService: IntegrationUtilService,
    dwSecurityStateService: DwSecurityStateService,
    paymentsRepositoryService: PaymentsRepositoryService,
    dwRoutingService: DwRoutingService,
    schoolRepositoryService: SchoolRepositoryService,
    private location: Location,
    private dwSecurityRegistrationRepositoryService: DwSecurityRegistrationRepositoryService,
    private hostElementRef: ElementRef<HTMLElement>,
    private hostRenderer: Renderer2,
    @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
    aviatorRegistrationDraftService: AviatorRegistrationDraftService,
    private phoneNumberFormatService: PhoneNumberFormatService
  ) {
    super(
      integrationUtilService,
      dwSecurityStateService,
      paymentsRepositoryService,
      dwRoutingService,
      schoolRepositoryService,
      aviatorRegistrationDraftService,
      hostElementRef,
      hostRenderer
    );
  }

  @ViewChild('registrationRef')
  set registrationComponentRef(registrationRef: RegistrationComponentLike) {
    this.registrationRef = registrationRef;
    this.restoreAccountDraft();
    this.syncRegistrationVerificationPayload();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.signupSecuritySubscription = this.dwSecurityStateService.securityStateObservable.subscribe(state => {
      this.signingIn = state.isAuthenticating || state.isAuthenticated;
    });
    this.loadRegistrationRestrictions();
    this.restorePersistedDraft();
    this.loadConditionalLookupItems();
    this.applyRegistrationStepFromPath();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setupDraftPersistenceListeners();
    this.restoreAccountDraft();
  }

  ngOnDestroy(): void {
    this.signupSecuritySubscription?.unsubscribe();
    this.draftListenerCleanup.forEach(cleanup => cleanup());
    this.draftListenerCleanup = [];
    this.stopResendCooldownTimer();
    super.ngOnDestroy();
  }

  get isVerificationCodeComplete(): boolean {
    return this.verificationCode.length === 6;
  }

  get canResendVerificationCode(): boolean {
    return this.resendCooldownSeconds === 0;
  }

  /* A step is complete once the flow has moved past it in the
     account → verification → profile sequence. */
  isRegistrationStepComplete(step: RegistrationStep): boolean {
    return this.registrationStepSequence.indexOf(this.registrationStep)
      > this.registrationStepSequence.indexOf(step);
  }

  getRegistrationStepFromPath(pathname: string): RegistrationStep {
    const path = (pathname || '').toLowerCase();

    if (path.indexOf('/register/verification') >= 0) {
      return 'verification';
    }

    if (path.indexOf('/register/profile') >= 0) {
      return 'profile';
    }

    return 'account';
  }

  setupStripeSession(stripeSessionDetails: StripeSessionDetailsResult) {
    super.setupStripeSession(stripeSessionDetails);

    if (stripeSessionDetails?.IsExistingUser) {
      this.dwRoutingService.navigateToUrl('/login', null);
    }
  }

  isFlightSchoolSelected(): boolean {
    return this.signupProfile.pilotTypeId === this.flightSchoolPilotTypeId;
  }

  get isDefaultCountrySelected(): boolean {
    return (this.signupProfile.countryId || this.defaultCountryId) === this.defaultCountryId;
  }

  get stateFieldLabel(): string {
    return this.isDefaultCountrySelected ? 'State' : 'State / Province';
  }

  get stateLookupId(): number {
    return this.isDefaultCountrySelected ? this.usStateLookupId : this.internationalStateLookupId;
  }

  get showStateFilter(): boolean {
    return !this.isDefaultCountrySelected;
  }

  get stateLookupParams(): number[] {
    return [this.signupProfile.countryId || this.defaultCountryId];
  }

  get stateLookupQueryParams(): DwQueryParam[] {
    return [{
      ParamName: 'CountryId',
      ParamValue: String(this.signupProfile.countryId || this.defaultCountryId)
    }];
  }

  goToVerificationStep(registrationRef: RegistrationComponentLike): void {
    const isAccountStepValid = registrationRef?.validateUserInfo
      ? registrationRef.validateUserInfo()
      : false;

    if (!isAccountStepValid) {
      return;
    }

    const registrationEmail = this.getCurrentRegistrationEmail();

    if (!registrationEmail) {
      this.setTopMessage('Enter a valid email address to continue.');
      return;
    }

    if (!this.isRegistrationEmailDomainAllowed(registrationEmail)) {
      this.setTopMessage(this.registrationEmailDomainMessage || 'Use an approved school email address to register.');
      return;
    }

    this.loading = true;
    this.loadingMessage = 'Validating email...';
    this.schoolRepositoryService.validateRegistrationEmail(registrationEmail)
      .pipe(take(1))
      .subscribe(() => {
        this.loading = false;
        this.continueToVerification(registrationRef, registrationEmail);
      }, error => {
        this.loading = false;
        this.setTopMessage(this.getRegistrationEmailValidationMessage(error));
      });
  }

  private continueToVerification(registrationRef: RegistrationComponentLike, registrationEmail: string): void {

    if (this.hasActiveVerificationSessionForEmail(registrationEmail)) {
      this.clearTopMessage();

      if (this.hasVerifiedRegistrationSession()) {
        this.goToProfileAfterVerification();
        return;
      }

      this.moveToVerificationStep();
      return;
    }

    this.clearTopMessage();
    this.clearVerificationSession();
    this.loading = true;
    this.loadingMessage = 'Sending verification code...';

    this.dwSecurityRegistrationRepositoryService.startRegistration(this.buildStartRegistrationRequest(registrationRef))
      .subscribe((response) => {
        this.loading = false;

        if (response?.Success && response?.RegistrationSessionToken) {
          this.startVerificationSession(response.RegistrationSessionToken, registrationEmail);
          this.moveToVerificationStep();
          return;
        }

        this.setTopMessage(
          response?.FailureMessage
          || (response?.EmailAlreadyExists ? 'An account with that email already exists.' : 'Unable to send the verification code right now.')
        );
      }, () => {
        this.loading = false;
        this.setTopMessage('Unable to send the verification code right now.');
      });
  }

  backToAccountStep(): void {
    this.registrationStep = 'account';
    this.profileValidationAttempted = false;
    this.profileFieldErrors = {};
    this.resetResendState();
    this.persistRegistrationDraft();
    this.location.replaceState('/register');
  }

  backToVerificationStep(): void {
    if (!this.hasActiveVerificationSession()) {
      this.handleExpiredVerificationSession();
      return;
    }

    this.registrationStep = 'verification';
    this.profileValidationAttempted = false;
    this.profileFieldErrors = {};
    this.resetResendState();
    this.persistRegistrationDraft();
    this.location.replaceState('/register/verification');
  }

  onRegistrationValidationFailed(validationFailure?: { errors?: string[]; missingFields?: string[] }): void {
    this.registrationStep = 'account';
    this.profileValidationAttempted = false;
    this.profileFieldErrors = {};

    const frameworkShowsValidationMessage = !!validationFailure
      && ((validationFailure.errors || []).length > 0 || (validationFailure.missingFields || []).length > 0);

    if (frameworkShowsValidationMessage) {
      this.clearTopMessage();
    } else {
      this.setTopMessage('Please complete the account details and required verification to continue.');
    }

    this.persistRegistrationDraft();
    this.location.replaceState('/register');
  }

  private submitResolvedRegistration(registrationRef: RegistrationComponentLike): void {
    if (registrationRef?.registerUserInternal) {
      registrationRef.registerUserInternal().pipe(take(1))
        .subscribe((attemptResult) => this.handleRegistrationAttemptResult(attemptResult));
      return;
    }

    registrationRef?.registerUser?.();
  }

  private handleRegistrationAttemptResult(attemptResult?: RegisterUserAttemptResultLike | null): void {
    if (!attemptResult || attemptResult.reason === 'processing') {
      return;
    }

    if (attemptResult.result?.Result === 'EmailVerificationRequired') {
      const message = attemptResult.result?.FailureMessage || 'Registration email verification is required.';

      if (this.hasActiveVerificationSession()) {
        this.moveToVerificationStep();
        this.verificationMessage = message;
      } else {
        this.registrationStep = 'account';
        this.setTopMessage(message);
        this.location.replaceState('/register');
      }
      return;
    }

    if (attemptResult.reason === 'success') {
      return;
    }

    this.onRegistrationValidationFailed();
  }

  updateVerificationCode(value: string): void {
    this.verificationCode = (value || '').replace(/\D/g, '').slice(0, 6);
    this.verificationMessage = '';
    this.syncRegistrationVerificationPayload();
  }

  getVerificationCodeDigit(index: number): string {
    return this.verificationCode.charAt(index);
  }

  isVerificationCodeCellActive(index: number): boolean {
    return this.verificationCodeInputFocused && index === Math.min(this.verificationCode.length, this.verificationCodeSlots.length - 1);
  }

  /* Write the sanitized code back into the input (same pattern as the
     birthdate mask) so the field can never display more than the six digits
     the model holds. */
  onVerificationCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.updateVerificationCode(input.value);
    input.value = this.verificationCode;
  }

  resendVerificationCode(): void {
    if (!this.canResendVerificationCode || !this.verificationSession?.registrationSessionToken) {
      return;
    }

    this.verificationMessage = '';
    this.dwSecurityRegistrationRepositoryService.resendRegistrationEmailCode({
      RegistrationSessionToken: this.verificationSession.registrationSessionToken
    }).subscribe((response) => {
      if (!response?.Success) {
        this.verificationMessage = response?.FailureMessage || 'Unable to resend the verification code right now.';
        return;
      }

      if (response?.RegistrationSessionToken) {
        this.startVerificationSession(response.RegistrationSessionToken, this.verificationEmail || this.getCurrentRegistrationEmail());
      }

      this.resendConfirmationVisible = true;
      this.startResendCooldown();
      this.persistRegistrationDraft();
    }, () => {
      this.verificationMessage = 'Unable to resend the verification code right now.';
    });
  }

  verifyEmailCode(): void {
    if (!this.isVerificationCodeComplete) {
      this.verificationMessage = 'Enter the 6-digit code from the email to continue.';
      return;
    }

    if (!this.verificationSession?.registrationSessionToken) {
      this.handleExpiredVerificationSession();
      return;
    }

    this.verificationMessage = '';
    this.loading = true;
    this.loadingMessage = 'Verifying email...';

    this.dwSecurityRegistrationRepositoryService.verifyRegistrationEmail({
      RegistrationSessionToken: this.verificationSession.registrationSessionToken,
      VerificationCode: this.verificationCode
    }).subscribe((response: DwVerifyRegistrationEmailResult) => {
      this.loading = false;

      if (response?.Success && response?.RegistrationVerificationProof) {
        this.completeVerificationSession(response);
        this.goToProfileAfterVerification();
        return;
      }

      this.verificationMessage = response?.FailureMessage || 'The verification code is invalid. Try again.';
    }, () => {
      this.loading = false;
      this.verificationMessage = 'Unable to verify the code right now.';
    });
  }

  skipVerificationForTesting(): void {
    if (!this.allowRegistrationVerificationSkip) {
      return;
    }

    if (!this.hasActiveVerificationSession()) {
      this.handleExpiredVerificationSession();
      return;
    }

    this.verificationMessage = '';
    this.goToProfileStep();
  }

  completeSignup(registrationRef: RegistrationComponentLike): void {
    if (this.signingIn || registrationRef?.processing) {
      return;
    }

    if (!this.hasVerifiedRegistrationSession() && !this.canSkipRegistrationVerification()) {
      this.handleExpiredVerificationSession();
      return;
    }

    this.profileValidationAttempted = true;
    this.refreshConditionalFieldState();

    if (!this.validateSignupProfile()) {
      return;
    }

    this.syncRegistrationVerificationPayload();
    this.resolveStateId().pipe(take(1))
      .subscribe((stateResolved) => {
        if (!stateResolved) {
          return;
        }

        this.writeSignupProfileAdditionalData(registrationRef);
        this.submitResolvedRegistration(registrationRef);
      });
  }

  private loadRegistrationRestrictions(): void {
    this.schoolRepositoryService.getSchoolFeatures()
      .pipe(take(1))
      .subscribe(features => {
        const registrationTypeId = features?.RegistrationTypeKey === 'FlightInstructor'
          ? this.instructorRegistrationTypeId
          : features?.RegistrationTypeKey === 'StudentRequest'
            ? this.studentRegistrationTypeId
            : null;
        this.showRegistrationRoleSelection = registrationTypeId != null
          && features.AllowInstructorRegistrationRoleSelection === true;

        if (registrationTypeId != null && this.signupProfile.requestedJoinRequestTypeId == null) {
          this.signupProfile.requestedJoinRequestTypeId = registrationTypeId;
        }

        if (this.showRegistrationRoleSelection) {
          this.registrationHeading = 'Create your school account';
          this.registrationSubheading = `Join ${features.FlightSchoolName || 'your school'} as a student or flight instructor`;
        } else if (registrationTypeId === this.instructorRegistrationTypeId) {
          this.registrationHeading = 'Create your instructor account';
          this.registrationSubheading = `Join ${features.FlightSchoolName || 'your school'} as a flight instructor`;
        }

        this.restrictRegistrationEmailDomains = features?.RestrictRegistrationEmailDomains === true;
        this.allowedRegistrationEmailDomains = (features?.AllowedRegistrationEmailDomains || [])
          .map(domain => this.normalizeRegistrationDomain(domain))
          .filter(domain => !!domain);

        if (!this.restrictRegistrationEmailDomains) {
          this.registrationEmailDomainMessage = '';
          return;
        }

        const domains = this.allowedRegistrationEmailDomains;
        this.registrationEmailDomainMessage = domains.length
          ? `Registration is limited to email addresses ending in ${domains.map(domain => `@${domain}`).join(' or ')}.`
          : 'Registration is limited to approved school email addresses.';
      });
  }

  selectRegistrationType(joinRequestTypeId: number): void {
    if (!this.showRegistrationRoleSelection
      || (joinRequestTypeId !== this.studentRegistrationTypeId && joinRequestTypeId !== this.instructorRegistrationTypeId)) {
      return;
    }

    this.signupProfile.requestedJoinRequestTypeId = joinRequestTypeId;
    this.persistRegistrationDraft();
  }

  private isRegistrationEmailDomainAllowed(email: string): boolean {
    if (!this.restrictRegistrationEmailDomains) {
      return true;
    }

    const normalizedEmail = (email || '').trim().toLowerCase();
    const atIndex = normalizedEmail.lastIndexOf('@');
    if (atIndex <= 0 || atIndex !== normalizedEmail.indexOf('@') || atIndex === normalizedEmail.length - 1) {
      return false;
    }

    const emailDomain = this.normalizeRegistrationDomain(normalizedEmail.substring(atIndex + 1));
    return !!emailDomain && this.allowedRegistrationEmailDomains.indexOf(emailDomain) >= 0;
  }

  private normalizeRegistrationDomain(domain: string): string {
    return (domain || '').trim().replace(/^@+/, '').toLowerCase();
  }

  private getRegistrationEmailValidationMessage(error: any): string {
    return error?.error?.Message
      || error?.error?.message
      || error?.error?.ErrorMessage
      || 'Use an approved school email address to register.';
  }

  hasProfileFieldError(field: SignupProfileFieldKey): boolean {
    return this.profileValidationAttempted && !!this.profileFieldErrors[field];
  }

  getProfileFieldError(field: SignupProfileFieldKey): string {
    if (!this.profileValidationAttempted) {
      return '';
    }

    return this.profileFieldErrors[field] || '';
  }

  onPilotTypeChanged(): void {
    if (!this.isFlightSchoolSelected()) {
      this.signupProfile.flightSchoolPositionId = null;
      delete this.profileFieldErrors['flightSchoolPositionId'];
    }

    this.refreshConditionalFieldState();
    this.persistRegistrationDraft();
  }

  onFlightSchoolPositionChanged(): void {
    this.refreshConditionalFieldState();
    this.persistRegistrationDraft();
  }

  onAcquisitionSourceChanged(): void {
    this.refreshConditionalFieldState();
    delete this.profileFieldErrors['acquisitionSourceId'];
    delete this.profileFieldErrors['acquisitionSourceOther'];
    this.persistRegistrationDraft();
  }

  onAviationGoalChanged(): void {
    delete this.profileFieldErrors['aviationGoalId'];
    this.persistRegistrationDraft();
  }

  onOutsideUsClick(): void {
    this.showCountryLookup = true;
    this.persistRegistrationDraft();
  }

  onCountryChanged(countryId: number | null): void {
    this.signupProfile.countryId = countryId || this.defaultCountryId;
    this.signupProfile.stateId = null;
    this.signupProfile.state = '';
    delete this.profileFieldErrors['state'];
    this.persistRegistrationDraft();
  }

  onStateInputChanged(): void {
    this.signupProfile.stateId = null;
    delete this.profileFieldErrors['state'];
    this.persistRegistrationDraft();
  }

  onStateLookupChanged(stateId: number | null): void {
    this.signupProfile.stateId = stateId;
    delete this.profileFieldErrors['state'];
    this.persistRegistrationDraft();
  }

  onBirthDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskBirthDate(input.value);

    this.signupProfile.birthDate = masked;
    input.value = masked;
    this.persistRegistrationDraft();
  }

  onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formattedValue = this.phoneNumberFormatService.formatForInput(input.value);

    this.signupProfile.phoneNumber = formattedValue;
    this.syncRegistrationMobilePhone(formattedValue);
    input.value = formattedValue;
    this.persistRegistrationDraft();
  }

  private goToProfileAfterVerification(): void {
    if (!this.hasVerifiedRegistrationSession()) {
      this.handleExpiredVerificationSession();
      return;
    }

    this.goToProfileStep();
  }

  private goToProfileStep(): void {
    this.registrationStep = 'profile';
    this.profileValidationAttempted = false;
    this.profileFieldErrors = {};
    this.resetResendState();
    this.syncRegistrationVerificationPayload();
    this.persistRegistrationDraft();
    this.location.replaceState('/register/profile');
  }
  private moveToVerificationStep(): void {
    if (!this.hasActiveVerificationSession()) {
      this.handleExpiredVerificationSession();
      return;
    }

    this.registrationStep = 'verification';
    this.verificationCode = '';
    this.verificationMessage = '';
    this.profileValidationAttempted = false;
    this.profileFieldErrors = {};
    this.resetResendState();
    this.syncRegistrationVerificationPayload();
    this.persistRegistrationDraft();
    this.location.replaceState('/register/verification');
  }

  private startResendCooldown(): void {
    this.stopResendCooldownTimer();
    this.resendCooldownSeconds = this.resendCooldownDurationSeconds;

    this.resendCooldownIntervalId = window.setInterval(() => {
      this.resendCooldownSeconds -= 1;

      if (this.resendCooldownSeconds <= 0) {
        this.resendCooldownSeconds = 0;
        this.stopResendCooldownTimer();
      }
    }, 1000);
  }

  private stopResendCooldownTimer(): void {
    if (this.resendCooldownIntervalId != null) {
      window.clearInterval(this.resendCooldownIntervalId);
      this.resendCooldownIntervalId = null;
    }
  }

  private resetResendState(): void {
    this.stopResendCooldownTimer();
    this.resendCooldownSeconds = 0;
    this.resendConfirmationVisible = false;
  }

  /* Honor a deep link to a specific step (e.g. a refresh on
     /register/verification) but leave the draft-restored step untouched when
     we are not actually on a registration route (test harness, SSR, etc.). */
  private applyRegistrationStepFromPath(): void {
    const path = (window.location.pathname || '').toLowerCase();

    if (path.indexOf('/register') < 0) {
      return;
    }

    const requestedStep = this.getRegistrationStepFromPath(path);

    if (requestedStep === 'account') {
      this.registrationStep = 'account';
      return;
    }

    if (!this.hasActiveVerificationSession()) {
      this.registrationStep = 'account';
      this.dwRoutingService.navigateToUrl('/register', null);
      return;
    }

    if (requestedStep === 'profile' && !this.hasVerifiedRegistrationSession() && !this.canSkipRegistrationVerification()) {
      this.registrationStep = 'verification';
      this.dwRoutingService.navigateToUrl('/register/verification', null);
      return;
    }

    this.registrationStep = requestedStep;
  }

  private maskBirthDate(rawValue: string): string {
    const digits = rawValue.replace(/\D/g, '').slice(0, 8);

    let masked = digits.slice(0, 2);
    if (digits.length > 2) {
      masked += '/' + digits.slice(2, 4);
    }
    if (digits.length > 4) {
      masked += '/' + digits.slice(4, 8);
    }

    return masked;
  }

  private parseBirthDate(value: string): Date | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
    if (!match) {
      return null;
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return date;
  }

  private getAgeFromBirthDate(value: string): number | null {
    const birthDate = this.parseBirthDate(value);
    if (!birthDate) {
      return null;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDelta = today.getMonth() - birthDate.getMonth();

    if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }

    return age;
  }

  private validateSignupProfile(): boolean {
    const errors: { [key: string]: string } = {};

    this.profileRequiredFields.forEach(fieldInfo => {
      if (!this.getProfileValue(fieldInfo.field)) {
        errors[fieldInfo.field] = `${fieldInfo.label} is required`;
      }
    });

    if (this.isDefaultCountrySelected && !this.getStateFieldValue()) {
      errors['state'] = 'State is required';
    }

    if (this.isFlightSchoolSelected() && !this.getProfileValue('flightSchoolPositionId')) {
      errors['flightSchoolPositionId'] = 'Your role at the flight school is required';
    }

    if (this.showAcquisitionSourceOther && !this.getProfileValue('acquisitionSourceOther')) {
      errors['acquisitionSourceOther'] = 'Please tell us how you heard about AviatorPro';
    }

    if (this.requirePilotCertificateNumber && !this.getProfileValue('pilotCertificateNumber')) {
      errors['pilotCertificateNumber'] = 'Pilot certificate number is required';
    }

    if (this.requireInstructorCertificateNumber && !this.getProfileValue('instructorCertifcateNumber')) {
      errors['instructorCertifcateNumber'] = 'Instructor certificate number is required';
    }

    if (this.getProfileValue('birthDate') && !errors['birthDate']) {
      const age = this.getAgeFromBirthDate(this.signupProfile.birthDate);

      if (age == null) {
        errors['birthDate'] = 'Enter a valid date (MM/DD/YYYY)';
      } else if (age < 13) {
        errors['birthDate'] = 'You must be at least 13 years old';
      } else if (age > 120) {
        errors['birthDate'] = 'Enter a valid date of birth';
      }
    }

    this.profileFieldErrors = errors;

    return Object.keys(errors).length === 0;
  }

  private resolveStateId(): Observable<boolean> {
    if (!this.isDefaultCountrySelected && !this.getStateFieldValue()) {
      this.signupProfile.stateId = null;
      this.signupProfile.state = '';
      delete this.profileFieldErrors['state'];
      return of(true);
    }

    if (this.useStateLookup) {
      delete this.profileFieldErrors['state'];
      return of(true);
    }

    const states$ = this.dwMetaDataService.getLookup(this.stateLookupId, this.stateLookupQueryParams) || of([]);

    return states$.pipe(
      take(1),
      map((lookupItems: LookupDisplayItem[]) => {
        const matchedState = this.useStateLookup
          ? this.findStateById(lookupItems, this.signupProfile.stateId)
          : this.findMatchingState(lookupItems, this.signupProfile.state);

        if (!matchedState?.Id) {
          this.profileFieldErrors = {
            ...this.profileFieldErrors,
            state: this.isDefaultCountrySelected
              ? 'Enter a valid U.S. state'
              : 'Select a valid state or province'
          };
          return false;
        }

        this.signupProfile.stateId = matchedState.Id;
        this.signupProfile.state = this.getPreferredStateDisplay(matchedState);
        delete this.profileFieldErrors['state'];
        return true;
      })
    );
  }

  private writeSignupProfileAdditionalData(registrationRef: RegistrationComponentLike): void {
    if (!registrationRef.registrationExtenededData) {
      registrationRef.registrationExtenededData = {
        additionalData: [],
        additionalUserFields: []
      };
    }

    const registrationExtenededData = registrationRef.registrationExtenededData;
    const existingAdditionalData = registrationExtenededData.additionalData || [];
    const profileEntries = [{
      DataKey: 'ProfileExtendedData',
      DataValue: JSON.stringify(this.buildProfileExtendedData())
    }];

    const managedKeys = profileEntries.map(profileEntry => profileEntry.DataKey);

    registrationExtenededData.additionalData = existingAdditionalData
      .filter(additionalData => managedKeys.indexOf(additionalData.DataKey) < 0)
      .concat(profileEntries);

    if (!registrationExtenededData.additionalUserFields) {
      registrationExtenededData.additionalUserFields = [];
    }

    this.syncRegistrationMobilePhone(this.signupProfile.phoneNumber, registrationRef);
  }

  private getProfileValue(field: SignupProfileFieldKey): string {
    const value = this.signupProfile[field];

    return value == null ? '' : value.toString().trim();
  }

  private getStateFieldValue(): string {
    if (this.useStateLookup) {
      return this.signupProfile.stateId == null ? '' : String(this.signupProfile.stateId);
    }

    return this.getProfileValue('state');
  }

  private buildProfileExtendedData(): SignupProfileDraft {
    return {
      ...this.signupProfile,
      requestedJoinRequestTypeId: this.showRegistrationRoleSelection ? this.signupProfile.requestedJoinRequestTypeId : null,
      flightSchoolPositionId: this.isFlightSchoolSelected() ? this.signupProfile.flightSchoolPositionId : null,
      acquisitionSourceOther: this.showAcquisitionSourceOther ? this.signupProfile.acquisitionSourceOther : '',
      pilotCertificateNumber: this.requirePilotCertificateNumber ? this.signupProfile.pilotCertificateNumber : '',
      instructorCertifcateNumber: this.requireInstructorCertificateNumber ? this.signupProfile.instructorCertifcateNumber : ''
    };
  }

  private loadConditionalLookupItems(): void {
    const pilotTypes$ = this.dwMetaDataService.getLookup(this.pilotTypeLookupId) || of([]);
    const flightSchoolPositions$ = this.dwMetaDataService.getLookup(this.flightSchoolPositionLookupId) || of([]);

    pilotTypes$.pipe(take(1))
      .subscribe((lookupItems: PilotTypeLookupDisplayItem[]) => {
        this.pilotTypeLookupItems = lookupItems || [];
        this.refreshConditionalFieldState();
      });

    flightSchoolPositions$.pipe(take(1))
      .subscribe((lookupItems: FlightSchoolPositionLookupDisplayItem[]) => {
        this.flightSchoolPositionLookupItems = lookupItems || [];
        this.refreshConditionalFieldState();
      });
  }

  private getSelectedPilotType(): PilotTypeLookupDisplayItem | null {
    const selectedPilotTypeId = this.normalizeLookupId(this.signupProfile.pilotTypeId);

    if (selectedPilotTypeId == null) {
      return null;
    }

    return this.pilotTypeLookupItems.find(lookupItem =>
      this.normalizeLookupId(lookupItem?.Id) === selectedPilotTypeId) || null;
  }

  private getSelectedFlightSchoolPosition(): FlightSchoolPositionLookupDisplayItem | null {
    const selectedFlightSchoolPositionId = this.normalizeLookupId(this.signupProfile.flightSchoolPositionId);

    if (selectedFlightSchoolPositionId == null) {
      return null;
    }

    return this.flightSchoolPositionLookupItems.find(lookupItem =>
      this.normalizeLookupId(lookupItem?.Id) === selectedFlightSchoolPositionId) || null;
  }

  // Design decision: derive conditional UI state from the specific change
  // events and lookup loads that affect it, rather than template getters that
  // re-run on every Angular change-detection pass.
  private refreshConditionalFieldState(): void {
    this.showAcquisitionSourceOther = this.signupProfile.acquisitionSourceId === AcquisitionSourceAllItems.Other;

    const selectedPilotType = this.getSelectedPilotType();
    const selectedFlightSchoolPosition = this.getSelectedFlightSchoolPosition();
    const requireReferenceFields = this.coerceLookupBoolean(selectedPilotType?.RequireReferenceFields);
    const isInstructor = this.coerceLookupBoolean(selectedFlightSchoolPosition?.IsInstructor);

    this.requirePilotCertificateNumber = requireReferenceFields; // || (this.isFlightSchoolSelected() && isInstructor);
    this.requireInstructorCertificateNumber = this.isFlightSchoolSelected() && isInstructor;

    if (!this.showAcquisitionSourceOther) {
      this.signupProfile.acquisitionSourceOther = '';
      delete this.profileFieldErrors['acquisitionSourceOther'];
    }

    if (!this.requirePilotCertificateNumber) {
      this.signupProfile.pilotCertificateNumber = '';
      delete this.profileFieldErrors['pilotCertificateNumber'];
    }

    if (!this.requireInstructorCertificateNumber) {
      this.signupProfile.instructorCertifcateNumber = '';
      delete this.profileFieldErrors['instructorCertifcateNumber'];
    }
  }

  private restorePersistedDraft(): void {
    const draft = this.aviatorRegistrationDraftService.loadDraft();

    if (!draft) {
      return;
    }

    this.persistedRegistrationDraft = draft;
    this.signupProfile = {
      ...this.signupProfile,
      ...draft.profile
    };
    this.signupProfile.phoneNumber = this.phoneNumberFormatService.formatForInput(this.signupProfile.phoneNumber);
    this.verificationSession = this.getValidVerificationSession(draft.verificationSession);
    this.verificationEmail = this.verificationSession?.email || draft.account?.email || '';
    this.registrationStep = this.getRestoredRegistrationStep(draft);
    this.showCountryLookup = draft.showCountryLookup
      || ((draft.profile?.countryId || this.defaultCountryId) !== this.defaultCountryId);
    this.refreshConditionalFieldState();
  }

  private restoreAccountDraft(): void {
    const draft = this.aviatorRegistrationDraftService.loadDraft() || this.persistedRegistrationDraft;

    if (!draft?.account || !this.registrationRef?.userRegistration) {
      return;
    }

    const currentRegistration = this.registrationRef.userRegistration;

    this.registrationRef.userRegistration = {
      ...currentRegistration,
      UserName: this.getPreferredAccountValue(currentRegistration.UserName, draft.account.userName),
      Email: this.getPreferredAccountValue(currentRegistration.Email, draft.account.email),
      FirstName: this.getPreferredAccountValue(currentRegistration.FirstName, draft.account.firstName),
      LastName: this.getPreferredAccountValue(currentRegistration.LastName, draft.account.lastName),
      LockEmail: !!currentRegistration.LockEmail || !!draft.account.lockEmail
    };

    const preferredMobilePhone = this.getPreferredAccountValue(currentRegistration.MobilePhone, this.signupProfile.phoneNumber);

    this.syncRegistrationMobilePhone(preferredMobilePhone);
    this.signupProfile.phoneNumber = preferredMobilePhone;

    if (this.verificationSession && !this.hasActiveVerificationSessionForEmail(this.registrationRef.userRegistration.Email)) {
      this.clearVerificationSession();
      this.registrationStep = 'account';
      this.replaceRegistrationRouteWithAccountStep();
    }

    if (this.registrationStep !== 'account' && !this.hasRestorableAccountSecurityState()) {
      this.registrationStep = 'account';
      this.replaceRegistrationRouteWithAccountStep();
    }
  }

  private setupDraftPersistenceListeners(): void {
    const onDraftFieldChanged = (event: Event) => {
      const target = event.target as HTMLElement | null;

      if (!target) {
        return;
      }

      if (target.closest('.ao-lookup__panel')) {
        return;
      }

      if (target instanceof HTMLInputElement && target.type === 'password') {
        return;
      }

      if (!this.hostElementRef.nativeElement.contains(target)) {
        return;
      }

      this.persistRegistrationDraft();
    };

    this.draftListenerCleanup.push(
      this.hostRenderer.listen(this.hostElementRef.nativeElement, 'input', onDraftFieldChanged),
      this.hostRenderer.listen(this.hostElementRef.nativeElement, 'change', onDraftFieldChanged)
    );
  }

  private persistRegistrationDraft(): void {
    const accountDraft = this.buildAccountDraft();
    const profileDraft = this.buildProfileExtendedData();

    this.aviatorRegistrationDraftService.saveDraft(
      accountDraft,
      profileDraft,
      this.registrationStep,
      this.showCountryLookup,
      this.verificationSession
    );
    this.persistedRegistrationDraft = this.aviatorRegistrationDraftService.loadDraft();
  }

  private buildAccountDraft(): RegistrationAccountDraft {
    const userRegistration: Partial<DwSecurityUserRegistration> = this.registrationRef?.userRegistration || {};

    return {
      userName: (userRegistration.UserName || '').trim(),
      email: (userRegistration.Email || '').trim(),
      firstName: (userRegistration.FirstName || '').trim(),
      lastName: (userRegistration.LastName || '').trim(),
      lockEmail: !!userRegistration.LockEmail
    };
  }

  private buildStartRegistrationRequest(registrationRef: RegistrationComponentLike): DwStartRegistrationRequest {
    const userRegistration = registrationRef?.userRegistration || {} as DwSecurityUserRegistration;

    return {
      Email: (userRegistration.Email || '').trim(),
      FirstName: (userRegistration.FirstName || '').trim() || undefined,
      LastName: (userRegistration.LastName || '').trim() || undefined,
      UserTypeId: this.getSelectedUserTypeId(registrationRef) || undefined,
      CaptchaResponse: userRegistration.CaptchaResponse
    };
  }

  private getSelectedUserTypeId(registrationRef: RegistrationComponentLike): number | null {
    const userTypeField = registrationRef?.registrationExtenededData?.additionalUserFields
      ?.find(userField => userField?.FieldName === 'UserTypeId');
    const userTypeId = Number(userTypeField?.FieldValue);

    return Number.isFinite(userTypeId) ? userTypeId : null;
  }

  private startVerificationSession(registrationSessionToken: string, email: string): void {
    this.verificationSession = {
      email,
      registrationSessionToken,
      expiresAtUtc: new Date(Date.now() + this.verificationSessionDurationMs).toISOString()
    };
    this.verificationEmail = email;
    this.syncRegistrationVerificationPayload();
  }

  private completeVerificationSession(result: DwVerifyRegistrationEmailResult): void {
    const currentEmail = this.verificationSession?.email || this.getCurrentRegistrationEmail();
    const registrationSessionToken = result?.RegistrationSessionToken || this.verificationSession?.registrationSessionToken;

    this.verificationSession = registrationSessionToken ? {
      email: currentEmail,
      registrationSessionToken,
      registrationVerificationProof: result.RegistrationVerificationProof,
      expiresAtUtc: new Date(Date.now() + this.verificationSessionDurationMs).toISOString()
    } : null;
    this.verificationEmail = currentEmail;
    this.syncRegistrationVerificationPayload();
    this.persistRegistrationDraft();
  }

  private getRestoredRegistrationStep(draft: RegistrationDraftState): RegistrationStep {
    if (!this.verificationSession) {
      return 'account';
    }

    if (draft.registrationStep === 'profile' && !this.hasVerifiedRegistrationSession() && !this.canSkipRegistrationVerification()) {
      return 'verification';
    }

    return draft.registrationStep || 'account';
  }

  private getValidVerificationSession(verificationSession?: RegistrationVerificationSession | null): RegistrationVerificationSession | null {
    if (!verificationSession?.registrationSessionToken || !verificationSession?.email) {
      return null;
    }

    if (this.isVerificationSessionExpired(verificationSession)) {
      return null;
    }

    return verificationSession;
  }

  private isVerificationSessionExpired(verificationSession?: RegistrationVerificationSession | null): boolean {
    const expiresAtMs = Date.parse(verificationSession?.expiresAtUtc || '');

    if (!Number.isFinite(expiresAtMs)) {
      return true;
    }

    return expiresAtMs <= Date.now();
  }

  private hasActiveVerificationSession(): boolean {
    return !!this.getValidVerificationSession(this.verificationSession);
  }

  private hasActiveVerificationSessionForEmail(email: string): boolean {
    return this.hasActiveVerificationSession()
      && this.normalizeEmail(this.verificationSession?.email) === this.normalizeEmail(email);
  }

  private hasVerifiedRegistrationSession(): boolean {
    return this.hasActiveVerificationSession() && !!this.verificationSession?.registrationVerificationProof;
  }

  private canSkipRegistrationVerification(): boolean {
    return this.allowRegistrationVerificationSkip && this.hasActiveVerificationSession();
  }

  private handleExpiredVerificationSession(): void {
    this.clearVerificationSession();
    this.registrationStep = 'account';
    this.verificationMessage = 'Your verification session expired. Please request a new code.';
    this.persistRegistrationDraft();
    this.dwRoutingService.navigateToUrl('/register', null);
  }

  private clearVerificationSession(): void {
    this.verificationSession = null;
    this.verificationEmail = this.getCurrentRegistrationEmail();
    this.verificationCode = '';
    this.verificationMessage = '';
    this.resetResendState();
    this.syncRegistrationVerificationPayload();
  }

  private syncRegistrationVerificationPayload(): void {
    if (!this.registrationRef?.userRegistration) {
      return;
    }

    this.registrationRef.userRegistration.RegistrationSessionToken = this.verificationSession?.registrationSessionToken;
    this.registrationRef.userRegistration.RegistrationVerificationProof = this.verificationSession?.registrationVerificationProof;
    this.registrationRef.userRegistration.EmailVerificationCode = this.hasVerifiedRegistrationSession() ? undefined : this.verificationCode || undefined;
  }

  private syncRegistrationMobilePhone(phoneNumber: string, registrationRef?: RegistrationComponentLike | null): void {
    const targetRegistration = registrationRef?.userRegistration || this.registrationRef?.userRegistration;

    if (!targetRegistration) {
      return;
    }

    targetRegistration.MobilePhone = (phoneNumber || '').trim();
  }

  private getCurrentRegistrationEmail(): string {
    return (this.registrationRef?.userRegistration?.Email || '').trim();
  }

  private hasRestorableAccountSecurityState(): boolean {
    const registration = this.registrationRef?.userRegistration;

    if (!registration) {
      return false;
    }

    return !!(registration.Password || '').trim() && !!registration.CaptchaResponse;
  }

  private replaceRegistrationRouteWithAccountStep(): void {
    const path = (window.location.pathname || '').toLowerCase();

    if (path.indexOf('/register') < 0) {
      return;
    }

    this.location.replaceState('/register');
  }

  private normalizeEmail(email?: string | null): string {
    return (email || '').trim().toLowerCase();
  }

  private setTopMessage(messageBody: string): void {
    this.topMessage = {
      messageBody,
      severity: DwMessageSeverity.error
    };
  }

  private clearTopMessage(): void {
    this.topMessage = null;
  }

  private getPreferredAccountValue(currentValue?: string, draftValue?: string): string {
    const normalizedCurrentValue = (currentValue || '').trim();
    return normalizedCurrentValue || (draftValue || '').trim();
  }

  private normalizeLookupId(value: string | number | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private coerceLookupBoolean(value: string | boolean | null | undefined): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    const normalizedValue = String(value || '').trim().toLowerCase();
    return normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes';
  }

  private findMatchingState(lookupItems: LookupDisplayItem[], stateText: string): LookupDisplayItem | null {
    const normalizedStateText = this.normalizeLookupText(stateText);

    if (!normalizedStateText) {
      return null;
    }

    return lookupItems.find(lookupItem => {
      const display = this.normalizeLookupText(lookupItem.Display);
      const shortName = this.normalizeLookupText(lookupItem.StateProvinceShortName);
      const fullName = this.normalizeLookupText(lookupItem.StateProvinceName);

      return normalizedStateText === display
        || normalizedStateText === shortName
        || normalizedStateText === fullName;
    }) || null;
  }

  private findStateById(lookupItems: LookupDisplayItem[], stateId: number | null): LookupDisplayItem | null {
    if (stateId == null) {
      return null;
    }

    return lookupItems.find(lookupItem => lookupItem?.Id === stateId) || null;
  }

  private getPreferredStateDisplay(lookupItem: LookupDisplayItem): string {
    if (!this.isDefaultCountrySelected) {
      return (lookupItem.StateProvinceName || lookupItem.Display || lookupItem.StateProvinceShortName || '').trim();
    }

    return (lookupItem.StateProvinceShortName || lookupItem.Display || lookupItem.StateProvinceName || '').trim();
  }

  private normalizeLookupText(value?: string | null): string {
    return (value || '').trim().toLowerCase();
  }
}
